import React, { useState } from 'react';
import {
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  Form,
  Row,
  Col,
  Input,
  Label,
} from 'reactstrap';
import { withTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Flatpickr from 'react-flatpickr';
import { Portuguese } from 'flatpickr/dist/l10n/pt.js';
import Wizard from '../../../Components/Common/Wizard';
import CurrencyInput from '../../../Components/Common/CurrencyInput';
import Select from 'react-select';
import campaignService from '../../../helpers/campaign_service';
import { getCampaignErrorMessage } from '../../../helpers/campaignErrorHandler';

const toNumber = (value) => {
  if (!value) return 0;
  const normalized = String(value).replace(/\./g, '').replace(',', '.');
  return parseFloat(normalized) || 0;
};

const Step1BasicInfo = ({
  formData,
  handleInputChange,
  handleDateChange,
  t,
}) => {
  return (
    <div className="d-flex flex-column gap-3">
      <div>
        <label className="form-label" htmlFor="campaign-name">
          {t('campaigns.form.campaign-name')} *
        </label>
        <input
          type="text"
          id="campaign-name"
          name="name"
          className="form-control"
          value={formData.name}
          onChange={handleInputChange}
          placeholder={t('campaigns.form.name-placeholder')}
          required
        />
      </div>

      <Row>
        <Col md="6">
          <label className="form-label" htmlFor="start-date">
            {t('campaigns.form.start-date')} *
          </label>
          <div className="flatpickr">
            <Flatpickr
              id="start-date"
              className="form-control"
              options={{
                locale: Portuguese,
                dateFormat: 'd/m/Y',
                allowInput: true,
              }}
              value={formData.startDate}
              onChange={(dates) => handleDateChange(dates, 'startDate')}
              placeholder={t('campaigns.form.date-placeholder')}
            />
          </div>
        </Col>
        <Col md="6">
          <label className="form-label" htmlFor="start-time">
            {t('campaigns.form.start-time')}
          </label>
          <div className="flatpickr">
            <Flatpickr
              id="start-time"
              className="form-control"
              options={{
                locale: Portuguese,
                enableTime: true,
                noCalendar: true,
                dateFormat: 'H:i',
                time_24hr: true,
              }}
              value={formData.startTime}
              onChange={(dates) => handleDateChange(dates, 'startTime')}
              placeholder={t('campaigns.form.time-placeholder')}
            />
          </div>
        </Col>
      </Row>

      <Row>
        <Col md="6">
          <label className="form-label" htmlFor="end-date">
            {t('campaigns.form.end-date')} *
          </label>
          <div className="flatpickr">
            <Flatpickr
              id="end-date"
              className="form-control"
              options={{
                locale: Portuguese,
                dateFormat: 'd/m/Y',
                allowInput: true,
              }}
              value={formData.endDate}
              onChange={(dates) => handleDateChange(dates, 'endDate')}
              placeholder={t('campaigns.form.date-placeholder')}
            />
          </div>
        </Col>
        <Col md="6">
          <label className="form-label" htmlFor="end-time">
            {t('campaigns.form.end-time')}
          </label>
          <div className="flatpickr">
            <Flatpickr
              id="end-time"
              className="form-control"
              options={{
                locale: Portuguese,
                enableTime: true,
                noCalendar: true,
                dateFormat: 'H:i',
                time_24hr: true,
              }}
              value={formData.endTime}
              onChange={(dates) => handleDateChange(dates, 'endTime')}
              placeholder={t('campaigns.form.time-placeholder')}
            />
          </div>
        </Col>
      </Row>
    </div>
  );
};

const Step2Accumulation = ({
  formData,
  handleInputChange,
  handleSelectChange,
  handleCheckboxChange,
  t,
}) => {
  const calculationBasisOptions = [
    { value: 'total', label: t('campaigns.form.calculation-total') },
    { value: 'paid', label: t('campaigns.form.calculation-paid') },
  ];

  const generationOptions = [
    { value: 'percentage', label: t('campaigns.form.percentage') },
    { value: 'fixed', label: t('campaigns.form.cashback-fixed') },
  ];

  const expirationTypeOptions = [
    { value: 'fixed', label: t('campaigns.form.expiration-fixed') },
  ];

  return (
    <div className="d-flex flex-column gap-4">
      <div>
        <h6 className="mb-3">
          {t('campaigns.form.configure-cashback-generation')}
        </h6>

        <Row className="mb-3">
          <Col md="4">
            <label className="form-label" htmlFor="generation-type">
              {t('campaigns.form.cashback-generation')} *
            </label>
            <Select
              id="generation-type"
              name="generation"
              value={generationOptions.find(
                (opt) => opt.value === formData.generation
              )}
              onChange={handleSelectChange}
              options={generationOptions}
              placeholder={t('select')}
              isClearable
            />
          </Col>
          <Col md="4">
            <label className="form-label" htmlFor="cashback-percentage">
              {t('campaigns.form.define-percentage')} *
            </label>
            <div className="input-group">
              <input
                type="text"
                id="cashback-percentage"
                name="cashbackPercentage"
                className="form-control"
                value={formData.cashbackPercentage}
                onChange={handleInputChange}
                placeholder="0,00"
              />
              <span className="input-group-text">%</span>
            </div>
          </Col>
          <Col md="4">
            <label className="form-label" htmlFor="calculation-basis">
              {t('campaigns.form.calculation-basis')} *
            </label>
            <Select
              id="calculation-basis"
              name="calculationBasis"
              value={calculationBasisOptions.find(
                (opt) => opt.value === formData.calculationBasis
              )}
              onChange={handleSelectChange}
              options={calculationBasisOptions}
              placeholder={t('select')}
              isClearable
            />
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md="4">
            <label className="form-label" htmlFor="min-purchase-value">
              {t('campaigns.form.minimum-purchase-value')}
            </label>
            <div className="input-group">
              <span className="input-group-text">R$</span>
              <CurrencyInput
                id="min-purchase-value"
                name="minimumPurchaseValue"
                value={formData.minimumPurchaseValue}
                onChange={(value) =>
                  handleInputChange({
                    target: { name: 'minimumPurchaseValue', value },
                  })
                }
                placeholder="0,00"
              />
            </div>
          </Col>
          <Col md="4">
            <label className="form-label" htmlFor="min-cashback">
              {t('campaigns.form.minimum-cashback-generated')}
            </label>
            <div className="input-group">
              <span className="input-group-text">R$</span>
              <CurrencyInput
                id="min-cashback"
                name="minimumCashbackGenerated"
                value={formData.minimumCashbackGenerated}
                onChange={(value) =>
                  handleInputChange({
                    target: { name: 'minimumCashbackGenerated', value },
                  })
                }
                placeholder="0,00"
              />
            </div>
          </Col>
          <Col md="4">
            <label className="form-label" htmlFor="max-cashback">
              {t('campaigns.form.maximum-cashback-generated')}
            </label>
            <div className="input-group">
              <span className="input-group-text">R$</span>
              <CurrencyInput
                id="max-cashback"
                name="maximumCashbackGenerated"
                value={formData.maximumCashbackGenerated}
                onChange={(value) =>
                  handleInputChange({
                    target: { name: 'maximumCashbackGenerated', value },
                  })
                }
                placeholder="0,00"
              />
            </div>
          </Col>
        </Row>
      </div>

      <div>
        <h6 className="mb-3">{t('campaigns.form.expiration-period')}</h6>

        <Row className="mb-3">
          <Col md="4">
            <label className="form-label" htmlFor="expiration-type">
              {t('campaigns.form.expiration-type')}
            </label>
            <Select
              id="expiration-type"
              name="expirationType"
              value={expirationTypeOptions.find(
                (opt) => opt.value === formData.expirationType
              )}
              onChange={(e) => handleSelectChange(e, 'expirationType')}
              options={expirationTypeOptions}
              placeholder={t('select')}
              isClearable
            />
          </Col>
          <Col md="4">
            <label className="form-label" htmlFor="expiration-days">
              {t('campaigns.form.expiration-days')}
            </label>
            <div className="input-group">
              <input
                type="number"
                id="expiration-days"
                name="expirationDays"
                className="form-control"
                value={formData.expirationDays}
                onChange={handleInputChange}
                placeholder="30"
              />
              <span className="input-group-text">
                {t('campaigns.form.days')}
              </span>
            </div>
          </Col>
        </Row>
      </div>

      <div>
        <h6 className="mb-3">{t('campaigns.form.availability-period')}</h6>

        <Row className="mb-3">
          <Col md="6">
            <label className="form-label" htmlFor="physical-store-days">
              {t('campaigns.form.physical-store-availability')}
            </label>
            <div className="input-group">
              <input
                type="number"
                id="physical-store-days"
                name="physicalStoreDays"
                className="form-control"
                value={formData.physicalStoreDays}
                onChange={handleInputChange}
                placeholder="10"
              />
              <span className="input-group-text">
                {t('campaigns.form.days')}
              </span>
            </div>
          </Col>
          <Col md="6">
            <label className="form-label" htmlFor="ecommerce-days">
              {t('campaigns.form.ecommerce-availability')}
            </label>
            <div className="input-group">
              <input
                type="number"
                id="ecommerce-days"
                name="ecommerceDays"
                className="form-control"
                value={formData.ecommerceDays}
                onChange={handleInputChange}
                placeholder="7"
              />
              <span className="input-group-text">
                {t('campaigns.form.days')}
              </span>
            </div>
          </Col>
        </Row>
      </div>

      <div>
        <h6 className="mb-3">{t('campaigns.form.other-settings')}</h6>

        <Row>
          <Col md="12">
            <div className="form-check form-switch mb-2">
              <Input
                type="checkbox"
                id="block-discounts-accumulation"
                name="blockDiscountsAccumulation"
                checked={formData.blockDiscountsAccumulation || false}
                onChange={handleCheckboxChange}
              />
              <Label
                className="form-check-label"
                for="block-discounts-accumulation"
              >
                {t('campaigns.form.block-accumulation-discount')}
              </Label>
            </div>
          </Col>
          <Col md="12">
            <div className="form-check form-switch mb-2">
              <Input
                type="checkbox"
                id="block-coupon-accumulation"
                name="blockCouponAccumulation"
                checked={formData.blockCouponAccumulation || false}
                onChange={handleCheckboxChange}
              />
              <Label
                className="form-check-label"
                for="block-coupon-accumulation"
              >
                {t('campaigns.form.block-accumulation-coupon')}
              </Label>
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
};

const Step3Redemption = ({
  formData,
  handleInputChange,
  handleCheckboxChange,
  t,
}) => {
  return (
    <div className="d-flex flex-column gap-4">
      <div>
        <h6 className="mb-3">
          {t('campaigns.form.configure-cashback-redemption')}
        </h6>

        <Row className="mb-3">
          <Col md="4">
            <label className="form-label" htmlFor="max-discount">
              {t('campaigns.form.maximum-discount')}
            </label>
            <div className="input-group">
              <input
                type="text"
                id="max-discount"
                name="maximumDiscount"
                className="form-control"
                value={formData.maximumDiscount}
                onChange={handleInputChange}
                placeholder="0,00"
              />
              <span className="input-group-text">%</span>
            </div>
          </Col>
          <Col md="4">
            <label className="form-label" htmlFor="max-redemption">
              {t('campaigns.form.maximum-redemption-value')}
            </label>
            <div className="input-group">
              <span className="input-group-text">R$</span>
              <CurrencyInput
                id="max-redemption"
                name="maximumRedemptionValue"
                value={formData.maximumRedemptionValue}
                onChange={(value) =>
                  handleInputChange({
                    target: { name: 'maximumRedemptionValue', value },
                  })
                }
                placeholder="0,00"
              />
            </div>
          </Col>
          <Col md="4">
            <label className="form-label" htmlFor="min-purchase-redemption">
              {t('campaigns.form.minimum-purchase-redemption')}
            </label>
            <div className="input-group">
              <span className="input-group-text">R$</span>
              <CurrencyInput
                id="min-purchase-redemption"
                name="minimumPurchaseRedemption"
                value={formData.minimumPurchaseRedemption}
                onChange={(value) =>
                  handleInputChange({
                    target: { name: 'minimumPurchaseRedemption', value },
                  })
                }
                placeholder="0,00"
              />
            </div>
          </Col>
        </Row>
      </div>

      <div>
        <h6 className="mb-3">{t('campaigns.form.block-settings')}</h6>

        <Row>
          <Col md="12">
            <div className="form-check form-switch mb-2">
              <Input
                type="checkbox"
                id="block-discounts-redemption"
                name="blockDiscountsRedemption"
                checked={formData.blockDiscountsRedemption || false}
                onChange={handleCheckboxChange}
              />
              <Label
                className="form-check-label"
                for="block-discounts-redemption"
              >
                {t('campaigns.form.block-redemption-discount')}
              </Label>
            </div>
          </Col>
          <Col md="12">
            <div className="form-check form-switch mb-2">
              <Input
                type="checkbox"
                id="block-coupon-redemption"
                name="blockCouponRedemption"
                checked={formData.blockCouponRedemption || false}
                onChange={handleCheckboxChange}
              />
              <Label className="form-check-label" for="block-coupon-redemption">
                {t('campaigns.form.block-redemption-coupon')}
              </Label>
            </div>
          </Col>
          <Col md="12">
            <div className="form-check form-switch mb-2">
              <Input
                type="checkbox"
                id="block-remarked-price"
                name="blockRemarkedPrice"
                checked={formData.blockRemarkedPrice || false}
                onChange={handleCheckboxChange}
              />
              <Label className="form-check-label" for="block-remarked-price">
                {t('campaigns.form.block-redemption-remarked')}
              </Label>
            </div>
          </Col>
        </Row>
      </div>

      <div>
        <h6 className="mb-3">{t('campaigns.form.notification-ruler')}</h6>
        <Row>
          <Col md="12">
            <div className="form-check form-switch mb-2">
              <Input
                type="checkbox"
                id="create-notification-config"
                name="createNotificationConfig"
                checked={formData.createNotificationConfig || false}
                onChange={handleCheckboxChange}
              />
              <Label
                className="form-check-label"
                for="create-notification-config"
              >
                {t('campaigns.form.notification-ruler-activate')}
              </Label>
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
};

const CreateCampaignWizard = (props) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    createNotificationConfig: false,
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    generation: '',
    cashbackPercentage: '',
    calculationBasis: '',
    minimumPurchaseValue: '',
    minimumCashbackGenerated: '',
    maximumCashbackGenerated: '',
    expirationType: '',
    expirationDays: '',
    physicalStoreDays: '',
    ecommerceDays: '',
    blockDiscountsAccumulation: false,
    blockCouponAccumulation: false,
    maximumDiscount: '',
    maximumRedemptionValue: '',
    minimumPurchaseRedemption: '',
    blockDiscountsRedemption: false,
    blockCouponRedemption: false,
    blockRemarkedPrice: false,
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDateChange = (dates, field) => {
    if (dates && dates.length > 0) {
      setFormData((prev) => ({
        ...prev,
        [field]: dates[0],
      }));
    }
  };

  const handleSelectChange = (selectedOption, actionMeta) => {
    const { name } = actionMeta;
    setFormData((prev) => ({
      ...prev,
      [name]: selectedOption?.value || '',
    }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleNext = () => {
    if (activeTab === 1) {
      const isStep1Valid =
        formData.name && formData.startDate && formData.endDate;
      console.log('Step 1 validation:', {
        isStep1Valid,
        name: formData.name,
        startDate: formData.startDate,
        endDate: formData.endDate,
      });
      if (!isStep1Valid) {
        toast.error(props.t('campaigns.form.fill-required-fields'));
        return;
      }
    } else if (activeTab === 2) {
      const isStep2Valid =
        formData.generation &&
        formData.cashbackPercentage &&
        formData.calculationBasis;
      console.log('Step 2 validation:', {
        isStep2Valid,
        generation: formData.generation,
        cashbackPercentage: formData.cashbackPercentage,
        calculationBasis: formData.calculationBasis,
      });
      if (!isStep2Valid) {
        toast.error(props.t('campaigns.form.fill-required-fields'));
        return;
      }
    }
    setActiveTab(activeTab + 1);
  };

  const handleBack = () => {
    setActiveTab(activeTab - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('handleSubmit called, activeTab:', activeTab);

    if (activeTab !== 3) {
      console.log('Preventing submit - not on step 3');
      return;
    }

    console.log('Submitting campaign...');
    setIsSubmitting(true);

    try {
      const combineDateAndTime = (date, time) => {
        if (!date) return null;
        let hours = 0,
          minutes = 0;

        if (time) {
          const timeObj = new Date(time);
          hours = timeObj.getHours();
          minutes = timeObj.getMinutes();
        }

        date.setHours(hours, minutes, 0, 0);

        return date;
      };

      const startDateTime = combineDateAndTime(
        formData.startDate,
        formData.startTime
      );
      const endDateTime = combineDateAndTime(
        formData.endDate,
        formData.endTime
      );

      const campaignData = {
        name: formData.name,
        createNotificationConfig: formData.createNotificationConfig,
        status: 'active',
        startDate: startDateTime,
        endDate: endDateTime,
        cashbackType: formData.generation,
        cashbackValue: toNumber(formData.cashbackPercentage),
        calculationBasis: formData.calculationBasis,
        minimumAccumulation: toNumber(formData.minimumCashbackGenerated),
        maximumAccumulation: toNumber(formData.maximumCashbackGenerated),
        minimumOrder: toNumber(formData.minimumPurchaseValue),
        daysToExpire: toNumber(formData.expirationDays),
        physicalStoreDays: toNumber(formData.physicalStoreDays),
        ecommerceDays: toNumber(formData.ecommerceDays),
        blockDiscounts: formData.blockDiscountsAccumulation,
        blockVtexAnyCoupon: formData.blockCouponAccumulation,
        rescueValue: toNumber(formData.maximumDiscount),
        maxRedemptionValue: toNumber(formData.maximumRedemptionValue),
        minimumPurchaseRedemption: toNumber(formData.minimumPurchaseRedemption),
        redemptionBlockDiscounts: formData.blockDiscountsRedemption,
        redemptionBlockVtexAnyCoupon: formData.blockCouponRedemption,
        redemptionBlockVtexPriceRemarked: formData.blockRemarkedPrice,
      };

      await campaignService.createCampaign(campaignData);

      setIsSubmitting(false);
      props.onSuccess();
      handleClose();
    } catch (error) {
      console.error('Error creating campaign:', error);
      setIsSubmitting(false);
      const errorMessage = getCampaignErrorMessage(error, props.t);
      toast.error(errorMessage);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      createNotificationConfig: false,
      startDate: '',
      startTime: '',
      endDate: '',
      endTime: '',
      generation: '',
      cashbackPercentage: '',
      calculationBasis: '',
      minimumPurchaseValue: '',
      minimumCashbackGenerated: '',
      maximumCashbackGenerated: '',
      expirationType: '',
      expirationDays: '',
      physicalStoreDays: '',
      ecommerceDays: '',
      blockDiscountsAccumulation: false,
      blockCouponAccumulation: false,
      maximumDiscount: '',
      maximumRedemptionValue: '',
      minimumPurchaseRedemption: '',
      blockDiscountsRedemption: false,
      blockCouponRedemption: false,
      blockRemarkedPrice: false,
    });
    setActiveTab(1);
    props.toggle();
  };

  const tabs = [
    {
      label: props.t('campaigns.wizard.step1'),
      component: (
        <Step1BasicInfo
          formData={formData}
          handleInputChange={handleInputChange}
          handleDateChange={handleDateChange}
          t={props.t}
        />
      ),
    },
    {
      label: props.t('campaigns.wizard.step2'),
      component: (
        <Step2Accumulation
          formData={formData}
          handleInputChange={handleInputChange}
          handleSelectChange={handleSelectChange}
          handleCheckboxChange={handleCheckboxChange}
          t={props.t}
        />
      ),
    },
    {
      label: props.t('campaigns.wizard.step3'),
      component: (
        <Step3Redemption
          formData={formData}
          handleInputChange={handleInputChange}
          handleCheckboxChange={handleCheckboxChange}
          t={props.t}
        />
      ),
    },
  ];

  return (
    <Modal
      centered
      isOpen={props.isOpen}
      size="xl"
      style={{ transition: 'all 0.2s ease' }}
      toggle={handleClose}
      id="campaignWizardModal"
    >
      <ModalHeader className="bg-info-subtle p-3" toggle={handleClose}>
        {props.t('campaigns.wizard.title')}
      </ModalHeader>

      <Form
        className="tablelist-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (activeTab < 3) {
            handleNext();
          } else {
            handleSubmit(e);
          }
          return false;
        }}
      >
        <ModalBody>
          <Wizard showSteps activeTab={activeTab} tabs={tabs} />
        </ModalBody>

        <ModalFooter className="border-top pt-3">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            {props.t('cancel')}
          </button>

          {activeTab > 1 && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleBack}
              disabled={isSubmitting}
            >
              {props.t('campaigns.wizard.back')}
            </button>
          )}

          {activeTab < 3 ? (
            <button
              type="button"
              className="btn btn-primary"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleNext();
              }}
            >
              {props.t('campaigns.wizard.next')}
              <i className="ri-arrow-right-line label-icon align-middle fs-16 ms-2"></i>
            </button>
          ) : (
            <button
              type="submit"
              className="btn btn-success"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  {props.t('saving')}
                </>
              ) : (
                <>
                  <i className="ri-check-line label-icon align-middle fs-16 ms-2"></i>
                  {props.t('campaigns.wizard.submit')}
                </>
              )}
            </button>
          )}
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default withTranslation()(CreateCampaignWizard);
